function requirePositiveDimension(value, label) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${label} must be a positive number.`);
  }
}

export async function decodeImageBlob(blob) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        close: () => bitmap.close?.()
      };
    } catch {
      // Safari versions with partial createImageBitmap support may reject the
      // options object. Retry through the universally supported Image path.
    }
  }

  if (typeof Image === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    throw new Error('This browser cannot prepare the selected image for video.');
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.decoding = 'async';
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('The selected image could not be decoded.'));
      element.src = objectUrl;
    });

    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      close: () => {}
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function readVideoSourceDimensions(blob) {
  const decoded = await decodeImageBlob(blob);
  try { return { width: decoded.width, height: decoded.height }; }
  finally { decoded.close(); }
}

/** Fit the entire original inside the output. A different shape adds padding;
 * no original pixels are cropped away, including faces near an edge. */
export function calculateVideoContainFit(sourceWidth, sourceHeight, targetWidth, targetHeight) {
  for (const [value, label] of [[sourceWidth, 'Source width'], [sourceHeight, 'Source height'], [targetWidth, 'Target width'], [targetHeight, 'Target height']]) requirePositiveDimension(value, label);
  const scale = Math.min(targetWidth / sourceWidth, targetHeight / sourceHeight);
  const width = sourceWidth * scale;
  const height = sourceHeight * scale;
  return { x: (targetWidth - width) / 2, y: (targetHeight - height) / 2, width, height };
}

function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('The selected image could not be prepared for video.'));
      }
    }, 'image/png');
  });
}

/**
 * Decode the original Gallery Blob, fit the entire artwork inside the canvas, and
 * return a local Blob with the exact dimensions sent to the video model. An
 * already matching source is reused; resized output is encoded as PNG.
 */
export async function fitImageBlobToVideoCanvas(blob, targetWidth, targetHeight, { opaque = false } = {}) {
  if (!(blob instanceof Blob)) {
    throw new Error('A Gallery image Blob is required.');
  }
  requirePositiveDimension(targetWidth, 'Target width');
  requirePositiveDimension(targetHeight, 'Target height');

  const decoded = await decodeImageBlob(blob);
  try {
    if (decoded.width === targetWidth && decoded.height === targetHeight && !opaque) {
      return blob;
    }

    if (typeof document === 'undefined') {
      throw new Error('This browser cannot prepare the selected image for video.');
    }

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('This browser cannot prepare the selected image for video.');
    }

    const fit = calculateVideoContainFit(
      decoded.width,
      decoded.height,
      targetWidth,
      targetHeight
    );
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.fillStyle = '#000000';
    context.fillRect(0, 0, targetWidth, targetHeight);
    context.drawImage(
      decoded.source,
      0,
      0,
      decoded.width,
      decoded.height,
      fit.x,
      fit.y,
      fit.width,
      fit.height
    );

    return await canvasToPngBlob(canvas);
  } finally {
    decoded.close();
  }
}
